import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { PermissionNames, IBrandInfoContract, BrandAPI } from '@getgreenline/homi-shared'
import AppHeader from '../../../components/AppView/Header'
import { AppView, AppMainView } from '../../../components/AppView/index'
import { NoPermissionsResult } from '../../../components/NoPermissionsResult'
import { HelpLinkMenu } from '../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'
import Helmet from 'react-helmet'
import Table, { ColumnProps } from 'antd-v4/lib/table'
import { Link } from 'react-router'
import { message, Button } from 'antd'
import { parseErrorMsg } from '../../../utilities/helpers'

import { Dropdown, Menu, Skeleton } from 'antd-v4'
import { SubmitBrandModal } from './SubmitBrandModal'
import { HelpLinks } from '../../../constants/ExternalLinks'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface State {
  isModalVisible: boolean
  brands?: IBrandInfoContract[]
  selectedBrand?: IBrandInfoContract
}

interface BrandListTableRecord {
  brand: JSX.Element
  licensedProducerName: string
  option?: JSX.Element
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class DashboardBrands extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      isModalVisible: false,
      brands: undefined,
      selectedBrand: undefined,
    }
  }

  componentDidMount = async () => this.getBrands()

  getBrands = async () => {
    try {
      const response = await BrandAPI.getBrandInfos(this.props.currentCompanyStore!.company!.id)

      this.setState({ brands: response })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while retrieving brands.')
    }
  }

  get tableColumns(): ColumnProps<BrandListTableRecord>[] {
    return [
      {
        title: 'Brand',
        dataIndex: 'brand',
        key: 'brand',
      },
      {
        title: 'Licensed producer',
        dataIndex: 'licensedProducerName',
        key: 'licensedProducerName',
      },
      {
        title: '',
        dataIndex: 'options',
        key: 'options',
        width: '92px',
      },
    ]
  }

  get canEditBrand(): boolean {
    const company = this.props.currentCompanyStore!.company!

    return this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_BRANDS,
      company.id,
    )
  }

  get tableDataSource(): BrandListTableRecord[] {
    const { brands } = this.state

    return (brands || []).map((brand, index) => {
      return {
        key: index,
        brand: (
          <Link to='' onClick={() => this.setState({ selectedBrand: brand })}>
            {brand.name}
          </Link>
        ),
        licensedProducerName: brand.licensedProducerName || '',
        options: this.canEditBrand && (
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu>
                <Menu.Item
                  key='edit'
                  onClick={() =>
                    this.setState({
                      selectedBrand: brand,
                      isModalVisible: true,
                    })
                  }
                >
                  <span style={{ color: 'var(--blue)' }}>Edit</span>
                </Menu.Item>

                <Menu.Item key='delete' onClick={() => this.setState({})}>
                  <span style={{ color: 'var(--red)' }}>Delete</span>
                </Menu.Item>
              </Menu>
            }
          >
            <Button icon='ellipsis' style={{ transform: 'rotate(90deg)' }} />
          </Dropdown>
        ),
      }
    })
  }

  render() {
    const company = this.props.currentCompanyStore!.company!

    if (
      !this.props.currentUserStore!.hasPermissionAtEntity(
        PermissionNames.CAN_VIEW_BRANDS,
        company.id,
      )
    ) {
      return <NoPermissionsResult permissionName={PermissionNames.CAN_VIEW_BRANDS} />
    }

    const { isModalVisible, brands, selectedBrand } = this.state

    return (
      <AppView column>
        <Helmet>
          <title>Products | Brands</title>
          <meta name='page-loaded' content='true' />
        </Helmet>

        <AppHeader title='Brands'>
          <span className='spaced-button-wrapper'>
            <HelpLinkMenu
              helpLinkMenus={{
                href: HelpLinks.PRODUCTS_BRANDS_VENDORS,
                title: 'Adding brands',
              }}
            />

            <BlazeButton
              buttonProps={{
                type: 'primary',
                icon: 'plus',
                onClick: () => this.setState({ isModalVisible: !isModalVisible }),
                disabled: !this.canEditBrand,
              }}
              titleProps={{
                title: 'Add brand',
              }}
            />
          </span>
        </AppHeader>
        <AppMainView className='background-grey'>
          {brands ? (
            <Table
              dataSource={this.tableDataSource}
              columns={this.tableColumns}
              className='no-background-table'
              rowClassName={() => 'background-white'}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          ) : (
            <Skeleton active />
          )}
        </AppMainView>

        {isModalVisible && (
          <SubmitBrandModal
            isModalVisible={isModalVisible}
            selectedBrand={selectedBrand}
            closeModal={() =>
              this.setState({
                isModalVisible: false,
                selectedBrand: undefined,
              })
            }
            getBrands={this.getBrands}
          />
        )}
      </AppView>
    )
  }
}
